import type { TokenResponseExtended as TokenResponse } from "owa-service/lib/tokenResponseExtended";
import type { IGetTokenApi } from "./IGetTokenApi";
import type { EnterpriseTokenRequestParams, ConsumerTokenRequestParams, ShadowMailboxTokenRequestParams, TokenRequestParams, } from "owa-tokenprovider-utils";
import { getAuthTokenResponse } from "./utils/getAuthTokenResponse";
import type { AuthTokenResponse } from "./schema/AuthTokenResponse";
import { fetchTokenFromServerWithRetry } from "./utils/fetchTokenFromServer";
import { fetchTokenFromMSA } from "./utils/fetchTokenFromMSA";
import type { ITokenQueue } from "./utils/ITokenQueue";
import type { ITokenCache } from "./utils/ITokenCache";
import { ScenarioType } from "owa-tokenprovider-utils";
import { getGuid } from "owa-guid";
import { getOrigin } from "owa-url/lib/getOrigin";
import { AUTHORIZATION_URI, CLIENT_ID, NUM_OF_RETRIES, RETRY_DELAY, RETURN_URL_PATH, } from "./const/constants";
import { DatapointStatus } from "owa-analytics-types";
import { getTokenLatencyStore } from "./getTokenLatencyStore";
import { getModuleContextMailboxInfo } from "owa-module-context-mailboxinfo";
import { isBootFeatureEnabled } from "owa-metatags";
import { type UnifiedTokenCache } from "auth-token-cache";
import type { MailboxInfo } from "owa-client-types";
import { noopTokenPerformanceLogger, type TokenPerformanceLogger, } from "./utils/TokenPerformanceLogger";

export class GetTokenApi implements IGetTokenApi {
    public tokenQueue: ITokenQueue;
    public tokenCache: ITokenCache;
    public unifiedCache: UnifiedTokenCache;
    
    public readonly LoggerPrefix: string = "GetTokenApi";
    
    constructor(queue: ITokenQueue, cache: ITokenCache, unifiedCache: UnifiedTokenCache) {
        this.tokenQueue = queue;
        this.tokenCache = cache;
        this.unifiedCache = unifiedCache;
    }
    
    protected createDatapoint(_mailboxInfo: MailboxInfo | undefined): TokenPerformanceLogger {
        return noopTokenPerformanceLogger;
    }
    
    public async getToken(params: TokenRequestParams): Promise<AuthTokenResponse | undefined> {
        params.requestId = params.requestId || getGuid();
        // Token provider telemetry: Using getModuleContextMailboxInfo as fallback because
        // token requests are account-specific operations for the current module's account context.
        const perfLog = this.createDatapoint(params.mailboxInfo ?? getModuleContextMailboxInfo());
        
        this.logParams(params, perfLog);
        
        switch (params.scenarioType) {
            case ScenarioType.ShadowMailbox:
                return this.getTokenForShadowMailbox(params, perfLog);
            
            case ScenarioType.Consumer:
                return this.getTokenForConsumer(params as ConsumerTokenRequestParams, perfLog);
            
            case ScenarioType.Enterprise:
                return this.getTokenForEnterprise(params as EnterpriseTokenRequestParams, perfLog);
        }
    }
    
    public async getTokenForEnterprise(enterpriseParams: EnterpriseTokenRequestParams, perfLog: TokenPerformanceLogger): Promise<AuthTokenResponse | undefined> {
        let tokenResponse: TokenResponse | undefined;
        let cacheKey: string = "";
        const startTime = Date.now();
        
        const latencyStore = getTokenLatencyStore();
        
        // Avoid fetching from cache if wwwAuthenticateHeader or claims are passed
        if (!enterpriseParams.wwwAuthenticateHeader && !enterpriseParams.claims) {
            if (isBootFeatureEnabled("auth-unifiedCache")) {
                // Only use cache if we have explicit mailboxInfo
                if (enterpriseParams.mailboxInfo) {
                    cacheKey = this.unifiedCache.getCacheKey(enterpriseParams.mailboxInfo, enterpriseParams.resource, enterpriseParams.scope, enterpriseParams.targetTenantId, undefined /* childAppId */);
                    tokenResponse = this.unifiedCache.getCachedToken(cacheKey);
                }
            }
            else {
                cacheKey = this.tokenCache.getCacheKey(enterpriseParams);
                tokenResponse = this.tokenCache.getCachedToken(cacheKey);
            }
        }
        
        if (tokenResponse) {
            perfLog.addCustomProperty(`${this.LoggerPrefix}EnterpriseCache_Success`, true);
            perfLog.end();
            latencyStore.setLatency(self, cacheKey, Date.now() - startTime);
            return getAuthTokenResponse(tokenResponse);
        }
        
        // Enqueue request to TokenQueue for repeated calls
        const promiseForResource = this.tokenQueue.enqueueRequestIfNotCached(cacheKey, enterpriseParams, this.getTokenForEnterpriseTask, perfLog);
        
        return promiseForResource
            .then(token => {
            this.tokenQueue.deleteCachedPromise(cacheKey);
            
            if (token) {
                perfLog.addCustomProperty(`${this.LoggerPrefix}Enterprise_Success`, true);
                latencyStore.setLatency(self, cacheKey, Date.now() - startTime);
            }
            else {
                perfLog.addCustomProperty(`${this.LoggerPrefix}Enterprise_NullToken`, true);
            }
            
            perfLog.end();
            return getAuthTokenResponse(token);
        })
            .catch(() => {
            this.tokenQueue.deleteCachedPromise(cacheKey);
            perfLog.endWithError(DatapointStatus.ClientError);
            return undefined;
        });
    }
    
    /*
    This is a workaround to be able to pass this function by reference to TokenQueue
    Original callback has to be on the prototype change for `super` calls as fallback
    */
    public getTokenForEnterpriseTask = (params: TokenRequestParams, perfLog: TokenPerformanceLogger): Promise<TokenResponse> => this.getTokenForEnterpriseCallback(params, perfLog);
    
    public async getTokenForEnterpriseCallback(params: TokenRequestParams, perfLog: TokenPerformanceLogger): Promise<TokenResponse> {
        const enterpriseParams = params as EnterpriseTokenRequestParams;
        
        perfLog.addCustomProperty(`${this.LoggerPrefix}EnterpriseCallback`, true);
        const tokenResponse = await fetchTokenFromServerWithRetry(perfLog, NUM_OF_RETRIES, RETRY_DELAY, enterpriseParams);
        
        let cacheKey: string = "";
        if (isBootFeatureEnabled("auth-unifiedCache")) {
            // Only cache if we have explicit mailboxInfo
            if (enterpriseParams.mailboxInfo) {
                cacheKey = this.unifiedCache.getCacheKey(enterpriseParams.mailboxInfo, enterpriseParams.resource, enterpriseParams.scope, enterpriseParams.targetTenantId, undefined /* childAppId */);
                this.unifiedCache.putCachedToken(cacheKey, true, tokenResponse);
            }
        }
        else {
            cacheKey = this.tokenCache.getCacheKey(enterpriseParams);
            this.tokenCache.putCachedToken(cacheKey, enterpriseParams, tokenResponse);
        }
        
        return tokenResponse;
    }
    
    public async getTokenForConsumer(consumerParams: ConsumerTokenRequestParams, perfLog: TokenPerformanceLogger): Promise<AuthTokenResponse | undefined> {
        let tokenResponse: TokenResponse | undefined;
        let cacheKey: string = "";
        const startTime = Date.now();
        
        if (isBootFeatureEnabled("auth-unifiedCache")) {
            if (consumerParams.mailboxInfo) {
                cacheKey = this.unifiedCache.getCacheKey(consumerParams.mailboxInfo, consumerParams.resource, consumerParams.scope, consumerParams.targetTenantId, undefined /* childAppId */);
                tokenResponse = this.unifiedCache.getCachedToken(cacheKey);
            }
        }
        else {
            cacheKey = this.tokenCache.getCacheKey(consumerParams);
            tokenResponse = this.tokenCache.getCachedToken(cacheKey);
        }
        const latencyStore = getTokenLatencyStore();
        
        if (tokenResponse?.AccessToken) {
            perfLog.addCustomProperty(`${this.LoggerPrefix}ConsumerCache_Success`, true);
            perfLog.end();
            latencyStore.setLatency(self, cacheKey, Date.now() - startTime);
            return getAuthTokenResponse(tokenResponse);
        }
        else {
            // Queue request to AuthTokenQueue to avoid making repeated n/w call for same resource
            const promiseForResource = this.tokenQueue.enqueueRequestIfNotCached(cacheKey, consumerParams, this.getTokenForConsumerTask, perfLog);
            
            return promiseForResource
                .then(token => {
                this.tokenQueue.deleteCachedPromise(cacheKey);
                
                if (token) {
                    perfLog.addCustomProperty(`${this.LoggerPrefix}Consumer_Success`, true);
                    latencyStore.setLatency(self, cacheKey, Date.now() - startTime);
                }
                else {
                    perfLog.addCustomProperty(`${this.LoggerPrefix}Consumer_NullToken`, true);
                }
                
                perfLog.end();
                return getAuthTokenResponse(token);
            })
                .catch(() => {
                this.tokenQueue.deleteCachedPromise(cacheKey);
                perfLog.endWithError(DatapointStatus.ClientError);
                return undefined;
            });
        }
    }
    
    /*
    This is a workaround to be able to pass this function by reference to TokenQueue
    Original callback has to be on the prototype change for `super` calls as fallback
    */
    public getTokenForConsumerTask = (params: TokenRequestParams, perfLog: TokenPerformanceLogger): Promise<TokenResponse> => this.getTokenForConsumerCallback(params, perfLog);
    
    public async getTokenForConsumerCallback(params: TokenRequestParams, perfLog: TokenPerformanceLogger): Promise<TokenResponse> {
        const consumerParams = params as ConsumerTokenRequestParams;
        this.populateAuthUrlParams(consumerParams);
        
        const scopeRequests = consumerParams.scope && consumerParams.scope.length > 0;
        const fetchFromServer = !scopeRequests;
        
        let tokenResponse;
        if (fetchFromServer) {
            perfLog.addCustomProperty(`${this.LoggerPrefix}ConsumerCallback_FetchTokenFromServer`, true);
            tokenResponse = await fetchTokenFromServerWithRetry(perfLog, NUM_OF_RETRIES, RETRY_DELAY, consumerParams);
        }
        else {
            perfLog.addCustomProperty(`${this.LoggerPrefix}ConsumerCallback_FetchTokenFromMSA`, true);
            tokenResponse = await fetchTokenFromMSA(perfLog, consumerParams);
        }
        
        if (isBootFeatureEnabled("auth-unifiedCache")) {
            if (consumerParams.mailboxInfo) {
                const cacheKey = this.unifiedCache.getCacheKey(consumerParams.mailboxInfo, consumerParams.resource, consumerParams.scope, consumerParams.targetTenantId, undefined /* childAppId */);
                this.unifiedCache.putCachedToken(cacheKey, false, tokenResponse);
            }
        }
        else {
            const cacheKey = this.tokenCache.getCacheKey(consumerParams);
            this.tokenCache.putCachedToken(cacheKey, consumerParams, tokenResponse);
        }
        
        return tokenResponse;
    }
    
    private populateAuthUrlParams(params: ConsumerTokenRequestParams) {
        params.redirect_uri = getOrigin() + RETURN_URL_PATH;
        params.authorization_uri = AUTHORIZATION_URI;
        params.client_id = CLIENT_ID;
    }
    
    public getTokenForShadowMailbox(_params: ShadowMailboxTokenRequestParams, perfLog: TokenPerformanceLogger): Promise<AuthTokenResponse | undefined> {
        const err = new Error("Method not implemented.");
        perfLog.endWithError(DatapointStatus.ClientError);
        throw err;
    }
    
    private logParams(requestParams: TokenRequestParams, perfLog: TokenPerformanceLogger) {
        for (const [key, value] of Object.entries(requestParams)) {
            if (value && key !== "mailboxInfo") {
                perfLog.addCustomProperty(key, value);
            }
        }
    }
}
